/**
 * WebSocket 信令客户端
 * 负责与冰梭信令服务器通信，交换 WebRTC 连接信息
 */
export class SignalingClient {
  constructor(serverDomain) {
    this.serverDomain = serverDomain;
    this.ws = null;
    this.handlers = {};
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
  }

  /**
   * 连接信令服务器
   * @param {Object} auth - 认证参数 { appKey, timestamp, nonce, sign, transferId }
   */
  connect(auth = {}) {
    return new Promise((resolve, reject) => {
      const url = this._buildUrl(auth);
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        this._log('已连接');
        this.reconnectAttempts = 0;
        resolve();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this._log('收到消息:', data.type);
          this._dispatch(data.type, data);
        } catch (e) {
          console.error('[IceShuttle Signaling] 消息解析失败:', e);
        }
      };

      this.ws.onclose = () => {
        this._log('连接关闭');
        this._dispatch('disconnected', {});
        this._tryReconnect(auth);
      };

      this.ws.onerror = (error) => {
        console.error('[IceShuttle Signaling] 连接错误:', error);
        reject(new Error('信令服务器连接失败'));
      };
    });
  }

  /**
   * 构建连接 URL
   */
  _buildUrl(auth) {
    const protocol = this.serverDomain.startsWith('https') ? 'wss:' : 'ws:';
    const host = this.serverDomain.replace(/^https?:\/\//, '');
    let url = `${protocol}//${host}/ws/signaling`;
    const params = new URLSearchParams();
    if (auth.appKey) params.set('appKey', auth.appKey);
    if (auth.timestamp) params.set('timestamp', auth.timestamp);
    if (auth.nonce) params.set('nonce', auth.nonce);
    if (auth.sign) params.set('sign', auth.sign);
    if (auth.transferId) params.set('transferId', auth.transferId);
    if (auth.algorithm) params.set('algorithm', auth.algorithm);  // 传递签名算法
    const qs = params.toString();
    if (qs) url += '?' + qs;
    return url;
  }

  /**
   * 注册事件处理器
   */
  on(type, handler) {
    if (!this.handlers[type]) this.handlers[type] = [];
    this.handlers[type].push(handler);
  }

  off(type, handler) {
    if (this.handlers[type]) {
      this.handlers[type] = this.handlers[type].filter(h => h !== handler);
    }
  }

  /**
   * 发送消息
   */
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('[IceShuttle Signaling] 未连接，无法发送消息');
    }
  }

  // ---- P2P 信令方法 ----

  register(transferId, fileName, fileSize) {
    this.send({ type: 'register', transferId, fileName, fileSize });
  }

  join(transferId) {
    this.send({ type: 'join', transferId });
  }

  sendOffer(transferId, sdp) {
    this.send({ type: 'offer', transferId, sdp });
  }

  sendAnswer(transferId, sdp) {
    this.send({ type: 'answer', transferId, sdp });
  }

  sendCandidate(transferId, candidate) {
    this.send({ type: 'candidate', transferId, candidate });
  }

  sendComplete(transferId) {
    this.send({ type: 'complete', transferId });
  }

  sendCancel(transferId) {
    this.send({ type: 'cancel', transferId });
  }

  sendTransferMode(transferId, transferMode, reason) {
    this.send({ type: 'transfer_mode', transferId, transferMode, reason });
  }

  // ---- 安全中转信令方法 ----

  sendChunkInit(transferId, fileName, fileSize, totalChunks, chunkSize, firstChunkSize, receiveToken) {
    this.send({ type: 'oss_chunk_init', transferId, fileName, fileSize, totalChunks, chunkSize, firstChunkSize, receiveToken });
  }

  sendChunkReady(transferId, chunkIndex, chunkSize, totalChunks, uploadedChunks) {
    this.send({ type: 'oss_chunk_ready', transferId, chunkIndex, chunkSize, totalChunks, uploadedChunks });
  }

  sendChunkComplete(transferId, totalChunks) {
    this.send({ type: 'oss_chunk_complete', transferId, totalChunks });
  }

  /**
   * 断开连接
   */
  disconnect() {
    this.reconnectAttempts = this.maxReconnectAttempts;
    this.handlers = {};
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * 分发事件
   */
  _dispatch(type, data) {
    if (this.handlers[type]) {
      this.handlers[type].forEach(handler => handler(data));
    }
  }

  /**
   * 自动重连
   */
  _tryReconnect(auth) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;
    this._log(`${delay}ms 后尝试重连 (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    setTimeout(() => {
      this.connect(auth).catch(() => {});
    }, delay);
  }

  _log(...args) {
    console.log('[IceShuttle Signaling]', ...args);
  }
}
